<script setup lang="ts">
import { Component, computed, onMounted, ref, watch } from "vue";
import { styleZoom } from "@/common/utils/style";
import { styleToNumber } from "@/common/utils/style";
import draggable from "vuedraggable";
// blocks
import BlockData from "./blocks/block-data.vue";
import TextBlock from "./blocks/text-block.vue";
import TableBlock from "./blocks/table-block.vue";
//type
import type { ReportPageType } from "@/common/types/report";
// components
import { svgIcon } from "@/common/components";

interface ComponentsTypes {
  component: Component;
  props: any;
}

const props = defineProps<{
  avatar: any;
  company: any;
  footer: any;
  header: any;
  page: ReportPageType;
  pageIndex: number;
  selectedBlock: string | null;
  zoom: number;
  print?: boolean;
}>();

const emit = defineEmits([
  "block:move-down",
  "block:move-up",
  "block:remove",
  "block:select",
  "block:reorder",
  "block:update",
]);

const components = computed<{ [key: string]: ComponentsTypes }>(() => ({
  "block-data": {
    component: BlockData,
    props: {
      selectedBlock: props.selectedBlock,
      zoom: props.zoom,
    },
  },
  "block-text": {
    component: TextBlock,
    props: {
      selectedBlock: props.selectedBlock,
      zoom: props.zoom,
    },
  },
  "block-title": {
    component: TextBlock,
    props: {
      selectedBlock: props.selectedBlock,
      zoom: props.zoom,
    },
  },
  "block-table": {
    component: TableBlock,
    props: {
      selectedBlock: props.selectedBlock,
      zoom: props.zoom,
    },
  },
}));

const internalPage = ref<ReportPageType>({ ...props.page });

const AREA_PADDING = computed(() => {
  if (!internalPage.value.style.padding) return 0;

  return styleToNumber(internalPage.value.style.padding);
});

const AREA_WIDTH = computed(() => {
  if (!props.page.style.width) return 0;

  const pageWidth = styleToNumber(props.page.style.width);

  return pageWidth - AREA_PADDING.value * 2;
});

function getBlock(type: keyof typeof components.value): ComponentsTypes {
  return components.value[type];
}

function getBlockProps(type: keyof typeof components.value): any {
  return components.value[type].props;
}

watch(
  () => props.page,
  (newPage) => {
    internalPage.value = { ...newPage };
  },
);

onMounted(() => {
  internalPage.value = { ...props.page };
});
</script>

<template>
  <div
    class="page"
    :class="{
      print: props.print,
      has_header: header?.visible && !footer?.visible,
      has_footer: footer?.visible && !header?.visible,
    }"
    :style="styleZoom(internalPage.style, zoom)"
  >
    <template v-if="header?.visible">
      <div
        class="page-header"
        :class="{
          'no-image': !avatar && !company,
          'no-company': !company && avatar,
        }"
        :style="
          styleZoom(
            {
              'font-size': '16px',
            },
            zoom,
          )
        "
      >
        <template v-if="company">
          <div class="page-header-company">
            <div class="page-header-image">
              <img :src="company.avatar?.base64" alt="Logo" />
            </div>
          </div>
        </template>

        <div class="page-header-content">
          <div class="page-header-title">
            {{ header.title }}
          </div>
          <div class="page-header-subtitle">
            {{ header.subtitle }}
          </div>
        </div>

        <template v-if="avatar">
          <div class="page-header-image">
            <img :src="avatar.base64" alt="Logo" />
          </div>
        </template>
      </div>
    </template>

    <div class="page-content">
      <draggable
        v-model="internalPage.blocks"
        item-key="id"
        class="blocks-container"
        @end="emit('block:reorder', $event)"
        handle=".drag-handle"
      >
        <template #item="{ element: block }">
          <div
            :id="`${pageIndex}-${block.block_id}`"
            class="block"
            :class="{
              selected: selectedBlock === block.block_id,
            }"
            @click="emit('block:select', { page_index: pageIndex, block })"
          >
            <div class="block-controls">
              <!-- <div class="drag-handle">⠿</div> -->
              <button
                class="block-btn"
                :disabled="block.ordered === 0"
                @click.stop="emit('block:move-up', block.block_id)"
              >
                ↑
              </button>
              <button
                class="block-btn"
                :disabled="block.ordered === page.blocks.length - 1"
                @click.stop="emit('block:move-down', block.block_id)"
              >
                ↓
              </button>
              <button
                class="block-btn danger"
                title="Remover"
                @click.stop="emit('block:remove', block.block_id)"
              >
                <svg-icon icon="trash" size="1rem" />
              </button>
            </div>

            <template v-if="getBlock(block.type)">
              <component
                :is="getBlock(block.type).component"
                v-bind="{
                  ...getBlockProps(block.type),
                  block: block,
                }"
                @block:update="emit('block:update', $event)"
              />
            </template>
          </div>
        </template>
      </draggable>
    </div>

    <template v-if="footer?.visible">
      <div
        class="page-footer"
        :style="
          styleZoom(
            {
              position: 'absolute',
              bottom: `${AREA_PADDING}px`,
              left: `${AREA_PADDING}px`,
              width: `${AREA_WIDTH}px`,
              'font-size': '16px',
            },
            zoom,
          )
        "
      >
        <div class="page-footer-content">
          <div class="page-footer-title">
            {{ footer.title }}
          </div>
          <div class="page-footer-subtitle">
            {{ footer.subtitle }}
          </div>
        </div>

        <div class="page-footer-page">{{ pageIndex + 1 }}</div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.page {
  display: grid;
  grid-template-rows: auto 1fr auto;
  margin-inline: auto;
  margin-bottom: 20px;
  position: relative;
  transition: all 0.3s ease;

  &.has_header {
    grid-template-rows: auto 1fr;
  }

  &.has_footer {
    grid-template-rows: 1fr auto;
  }

  &.print {
    width: 210mm !important;
    height: 296.75mm !important;
    max-height: 296.75mm !important;
    overflow: hidden;
    border: 0px;
    font-size: 16px;
    margin-bottom: 0;
  }

  .page-header {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    border-bottom: 1px solid #ccc;
    padding-block-end: 0.25rem;
    margin-block-end: 0.5rem;

    &.no-image {
      grid-template-columns: 1fr auto;

      .page-header-content {
        align-items: flex-start;
      }
    }

    &.no-company {
      grid-template-columns: auto 1fr;

      .page-header-content {
        align-items: flex-end;
      }
    }

    .page-header-content {
      display: flex;
      flex-direction: column;
      align-items: center;

      .page-header-title {
        font-size: 0.875em;
        font-weight: bold;
      }

      .page-header-subtitle {
        font-size: 0.75em;
        color: #666;
      }
    }

    .page-header-company {
      display: flex;
      align-items: center;
    }

    .page-header-image {
      width: auto;
      height: 45px;
      border-radius: 4px;
      overflow: hidden;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
  }

  .page-content {
    display: flex;
    flex-direction: column;

    .block {
      position: relative;
      border: 1px solid transparent;
      transition: all 0.3s ease;

      &.selected {
        border-color: #007bff;
        background: #f8f9ff;

        .block-controls {
          opacity: 1;
          z-index: 99;
        }

        &:hover {
          border-color: #007bff;
        }
      }

      .block-controls {
        position: absolute;
        top: -30px;
        right: 0;
        display: flex;
        gap: 2px;
        opacity: 0;
        transition: opacity 0.3s ease;
        background: #fff;
        padding: 3px;
        z-index: -99;

        .drag-handle {
          padding: 2px 6px;
          background: #6c757d;
          color: white;
          cursor: grab;
          font-size: 12px;
        }

        .block-btn {
          padding: 2px 6px;
          background: #6c757d;
          color: white;
          border: none;
          font-size: 12px;
          cursor: pointer;

          &:hover {
            background: #545b62;
          }

          &:disabled {
            background: #ccc;
            cursor: not-allowed;
          }

          &.danger {
            margin-left: 8px;
            padding-inline: 2px;
          }

          &.danger:hover {
            background: #dc3545;
          }
        }
      }

      &:hover {
        border-color: #dee2e6;
      }
    }
  }

  .page-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-top: 1px solid #ccc;

    .page-footer-content {
      display: flex;
      flex-direction: column;

      .page-footer-title {
        font-size: 0.875em;
        font-weight: bold;
      }

      .page-footer-subtitle {
        font-size: 0.875em;
        color: #666;
      }
    }

    .page-footer-page {
      color: #666;
      font-size: 0.875em;
      font-weight: bold;
    }
  }
}
</style>
